import * as React from "react";
import { toSelectedSkillReference } from "../selection";
import { SKILL_RUN_ERRORS } from "../runtime/skillDecision";
import type { SelectedSkillReference, SkillDecision, SkillDecisionSkill } from "../types";

export interface MatchingSkillBannerState {
  decision: SkillDecision;
  prompt: string;
  /** True while the Run with / Run without button reads "Stopping previous run…". */
  stoppingRun: boolean;
  /** Shown when that run could not be stopped or started. The button stays available. */
  runError?: string;
}

export interface ReplaceSkillRunInput {
  prompt: string;
  selectedSkills: SelectedSkillReference[];
}

export interface MatchingSkillBannerController {
  state: MatchingSkillBannerState | null;
  /** Marks the submission whose resolve result is allowed to change the banner. */
  noteSubmission: (submissionId: string) => void;
  setDecision: (submissionId: string, decision: SkillDecision | null, prompt?: string) => void;
  dismiss: () => void;
  runWithSkills: (
    skills: readonly SkillDecisionSkill[],
    onReplaceSkillRun: (input: ReplaceSkillRunInput) => Promise<unknown>
  ) => Promise<void>;
  runWithoutSkills: (
    onReplaceSkillRun: (input: ReplaceSkillRunInput) => Promise<unknown>
  ) => Promise<void>;
}

export interface UseMatchingSkillBannerInput {
  isLoading: boolean;
}

/**
 * Removal of the strip, kept apart from the button's `stoppingRun` label.
 * Holding: the replace was clicked, and the false from deleting the old run
 * must not remove the strip.
 * Seen: the replacement query has been loading, so the next false removes it.
 */
type BannerRemoval = "idle" | "holding" | "seen";

/**
 * Composer-scoped matching-skill banner state. Visible only while a decision
 * is active. The strip stays through the moment the old run is deleted, and
 * leaves when the replacement query's loading flag ends, unless Run with /
 * Run without failed.
 *
 * Those buttons turn on "Stopping previous run…" and leave it on. That flag
 * is only the button label. An error turns it off and leaves the button
 * available.
 */
export function useMatchingSkillBanner({
  isLoading,
}: UseMatchingSkillBannerInput): MatchingSkillBannerController {
  const [state, setState] = React.useState<MatchingSkillBannerState | null>(null);
  const stateRef = React.useRef(state);
  React.useLayoutEffect(() => {
    stateRef.current = state;
  });
  const currentSubmissionIdRef = React.useRef<string | null>(null);
  const removalRef = React.useRef<BannerRemoval>("idle");

  const noteSubmission = (submissionId: string) => {
    currentSubmissionIdRef.current = submissionId;
  };

  const setDecision = (submissionId: string, decision: SkillDecision | null, prompt = "") => {
    if (submissionId !== currentSubmissionIdRef.current) return;
    if (decision == null || decision.skills.length === 0) {
      removalRef.current = "idle";
      setState(null);
      return;
    }
    setState({
      decision,
      prompt,
      stoppingRun: false,
    });
  };

  const dismiss = () => {
    currentSubmissionIdRef.current = null;
    removalRef.current = "idle";
    setState(null);
  };

  const runPromptAgain = async (
    selectedSkills: SelectedSkillReference[],
    onReplaceSkillRun: (input: ReplaceSkillRunInput) => Promise<unknown>
  ) => {
    const current = stateRef.current;
    if (!current) return;
    const { prompt } = current;
    removalRef.current = "holding";
    setState((prev) => {
      if (!prev) return prev;
      return { ...prev, stoppingRun: true, runError: undefined };
    });
    try {
      await onReplaceSkillRun({ prompt, selectedSkills });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : SKILL_RUN_ERRORS.couldNotStart;
      removalRef.current = "idle";
      setState((prev) => {
        if (!prev || !prev.stoppingRun) return prev;
        return { ...prev, stoppingRun: false, runError: message };
      });
    }
  };

  const runWithSkills = (
    skills: readonly SkillDecisionSkill[],
    onReplaceSkillRun: (input: ReplaceSkillRunInput) => Promise<unknown>
  ) => runPromptAgain(skills.map((skill) => toSelectedSkillReference(skill)), onReplaceSkillRun);

  const runWithoutSkills = (onReplaceSkillRun: (input: ReplaceSkillRunInput) => Promise<unknown>) =>
    runPromptAgain([], onReplaceSkillRun);

  React.useEffect(() => {
    if (stateRef.current?.runError) return;
    if (isLoading) {
      if (removalRef.current === "holding") removalRef.current = "seen";
      return;
    }
    if (removalRef.current === "holding") return;
    removalRef.current = "idle";
    setState((prev) => (prev?.runError ? prev : null));
  }, [isLoading]);

  return {
    state,
    noteSubmission,
    setDecision,
    dismiss,
    runWithSkills,
    runWithoutSkills,
  };
}
